import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { apiConfig } from '@betty/toolkit/api';
import { AsyncStorage } from '@betty/toolkit/storage';
import {
  appLogout,
  persistReducer,
  State,
  injectSlice,
} from '@betty/toolkit/state';
import { ChatState, LocalizationRequest } from './chat.types';
import {
  Chat,
  Form,
  Agent,
  Thread,
  Config,
  Customer,
  Visibility,
  FormMessage,
  Localization,
  EventMessage,
  DynamicConfig,
  ConfigRequest,
  ThreadsMessage,
  SessionVariables,
  TicketFormSettings,
  TicketFormSettingsRequest,
  EventPropertiesUpdatedMessage,
  IncomingTypingIndicatorMessage,
} from './types';
import ChatService from '../services/ChatService/ChatService';
import { Attachment } from '../components/ChatAttachMenu/ChatAttachMenu.types';
import {
  findChatAgent,
  sendChatAPIRequest,
  getHelpDeskLicenseId,
  sendHelpdeskAPIRequest,
} from './chat.utils.native';
import { selectAccount } from '@betty/account/state';

export const loadThreads = createAction('chat/loadThreads');

export const getForm = createAction<Form['type']>('chat/getForm');

export const formReceived = createAction<FormMessage['form'] | undefined>(
  'chat/formReceived',
);

export const startChat = createAction('chat/startChat');

export const stopChat = createAction('chat/stopChat');

export const sendMessage = createAction<string>('chat/sendMessage');

export const sendSneakPeek = createAction<string>('chat/sendSneakPeek');

export const sendAttachments = createAction<Attachment[]>(
  'chat/sendAttachments',
);

export const seenMessages = createAction<string>('chat/seenMessages');

export const rateApp = createAction('chat/rateApp');

export const updateThreadProperties = createAction<Record<string, object>>(
  'chat/updateThreadProperties',
);

export const reconnectChat = createAction('chat/reconnect');

export const renewSession = createAction('chat/renewSession');

export const groupUpdated = createAction('chat/groupUpdated');

export const uploadFile = createAsyncThunk<{ url: string }, FormData>(
  'chat/uploadFile',
  async (data) => {
    const response = await sendChatAPIRequest({
      url: '/customer/action/upload_file',
      method: 'POST',
      body: data,
    });

    return await response.json();
  },
);

export const fetchDynamicConfig = createAsyncThunk<DynamicConfig, number>(
  'chat/fetchDynamicConfig',
  async (license_id) => {
    const response = await sendChatAPIRequest({
      url: '/customer/action/get_dynamic_configuration',
      method: 'GET',
      params: { license_id },
    });

    return await response.json();
  },
);

export const fetchConfig = createAsyncThunk<Config, ConfigRequest>(
  'chat/fetchConfig',
  async (params) => {
    const response = await sendChatAPIRequest({
      url: '/customer/action/get_configuration',
      method: 'GET',
      params,
    });

    return await response.json();
  },
);

export const fetchTicketFormSettings = createAsyncThunk<
  TicketFormSettings,
  TicketFormSettingsRequest
>('chat/fetchTicketFormSettings', async (params) => {
  const response = await sendHelpdeskAPIRequest({
    url: '/contactForms/formSettings',
    method: 'GET',
    params,
  });

  return await response.json();
});

export const sendTicket = createAsyncThunk(
  'chat/sendTicket',
  async (data: Record<string, string>, thunkApi) => {
    const config = selectConfig(<State>thunkApi.getState());
    const settings = selectTicketFormSettings(<State>thunkApi.getState());

    if (!config || !settings) {
      throw new Error('Missing form config or settings');
    }

    const { name, email, subject, textarea: message } = data;

    await sendHelpdeskAPIRequest({
      url: '/externalTickets',
      method: 'POST',
      body: JSON.stringify({
        assignment: {
          agent: null,
          team: {
            ID: settings.teamID,
          },
        },
        isBooster: true,
        licenseID: getHelpDeskLicenseId(config),
        message: {
          text: message,
        },
        requester: {
          name,
          email,
        },
        subject,
        teamIDs: [settings.teamID],
      }),
    });
  },
);

export const fetchLocalization = createAsyncThunk<
  Localization,
  LocalizationRequest
>('chat/fetchLocalization', async (params) => {
  const response = await sendChatAPIRequest({
    url: '/customer/action/get_localization',
    method: 'GET',
    params,
  });

  return await response.json();
});

export const fetchCustomerToken = createAsyncThunk(
  'chat/fetchCustomerToken',
  async () => {
    const userId = await apiConfig.getUserId();
    const data = await ChatService.getCustomerToken({
      pathParams: {
        userId: userId || '-1',
      },
    });

    return data;
  },
);

export const updateSessionVariables = createAsyncThunk(
  'chat/updateSessionVariables',
  async (data: SessionVariables) => {
    const userId = await apiConfig.getUserId();

    await ChatService.updateSessionVariables({
      data,
      pathParams: {
        userId: userId || '-1',
      },
    });
  },
);

const initialState: ChatState = {
  auth: undefined,
  agent: undefined,
  chat: undefined,
  form: undefined,
  config: undefined,
  dynamicConfig: undefined,
  ticketFormSettings: undefined,
  next_page_id: undefined,
  events: [],
  typing: false,
  visibility: 'hidden',
  hasUnseenMessages: false,
  customer: undefined,
  sessionFieldsTS: undefined,
};

const chat = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    loggedIn(state, { payload }: PayloadAction<Customer>) {
      state.agent = undefined;
      state.chat = undefined;
      state.form = undefined;
      state.next_page_id = undefined;
      state.typing = false;
      state.events = [];
      state.customer = payload;
    },
    addEvent(state, { payload }: PayloadAction<EventMessage>) {
      const { event } = payload;

      if (state.chat) {
        state.chat.thread.events.push(event);
      }

      state.events.push(event);
      state.typing = false;
    },
    setEventProperties(
      state,
      { payload }: PayloadAction<EventPropertiesUpdatedMessage>,
    ) {
      const { event_id, properties } = payload;

      state.events.forEach((event) => {
        if (event.id === event_id && 'properties' in event) {
          event.properties = properties;
        }
      });
    },
    addThread(state, { payload }: PayloadAction<Thread>) {
      const { events } = payload;

      state.events.push(...events);
    },
    threadsLoaded(state, { payload }: PayloadAction<ThreadsMessage>) {
      const { threads, next_page_id } = payload;

      for (const thread of threads) {
        const { events } = thread;

        state.events.unshift(...events);
      }

      state.next_page_id = next_page_id;
    },
    setAgent(state, { payload }: PayloadAction<Agent>) {
      state.agent = payload;
    },
    setChat(state, { payload }: PayloadAction<Chat>) {
      state.chat = payload;
    },
    setForm(state, { payload }: PayloadAction<Form | undefined>) {
      state.form = payload;
    },
    setTyping(
      state,
      { payload }: PayloadAction<IncomingTypingIndicatorMessage>,
    ) {
      state.typing = payload.typing_indicator.is_typing;
    },
    setHasUnseenMessages(state, { payload }: PayloadAction<boolean>) {
      state.hasUnseenMessages = payload;
    },
    setSessionFieldsTS(state) {
      state.sessionFieldsTS = Date.now();
    },
    minimizeChat(state) {
      state.visibility = 'minimized';
    },
    maximizeChat(state) {
      state.visibility = 'maximized';
    },
    hideChat(state) {
      state.visibility = 'hidden';
    },
    setVisibility(state, { payload }: PayloadAction<Visibility>) {
      state.visibility = payload;
    },
    chatDeactivated(state) {
      if (state.chat) {
        state.chat.thread.active = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCustomerToken.fulfilled, (state, { payload }) => {
      state.auth = payload;
    });

    builder.addCase(fetchDynamicConfig.fulfilled, (state, { payload }) => {
      state.dynamicConfig = payload;
    });

    builder.addCase(fetchConfig.fulfilled, (state, { payload }) => {
      const { properties, ticket_form } = payload;

      state.config = {
        properties,
        ticket_form,
      };
    });

    builder.addCase(fetchTicketFormSettings.fulfilled, (state, { payload }) => {
      state.ticketFormSettings = payload;
    });

    builder.addCase(appLogout, () => {
      return initialState;
    });
  },
});

export const {
  addEvent,
  addThread,
  setAgent,
  setChat,
  setForm,
  loggedIn,
  hideChat,
  setTyping,
  minimizeChat,
  maximizeChat,
  threadsLoaded,
  chatDeactivated,
  setVisibility,
  setEventProperties,
  setSessionFieldsTS,
  setHasUnseenMessages,
} = chat.actions;

const persistConfig = {
  key: 'chat',
  storage: AsyncStorage,
  whitelist: ['sessionFieldsTS'],
};

const persistedReducer = persistReducer(persistConfig, chat.reducer);

export const withChat = injectSlice({
  reducerPath: 'chat',
  reducer: persistedReducer,
});

export const selectAuth = withChat.selector((state) => state.chat.auth);

export const selectDynamicConfig = withChat.selector(
  (state) => state.chat.dynamicConfig,
);

export const selectConfig = withChat.selector((state) => state.chat.config);

export const selectTicketFormSettings = withChat.selector(
  (state) => state.chat.ticketFormSettings,
);

export const selectChat = withChat.selector((state) => state.chat.chat);

export const selectIsChatActive = withChat.selector(
  (state) => !!state.chat.chat?.thread.active,
);

export const selectIsChatQueued = withChat.selector(
  (state) => !!state.chat.chat?.thread.queue,
);

export const selectForm = withChat.selector((state) => state.chat.form);

export const selectGroupId = createSelector(
  selectDynamicConfig,
  selectAccount,
  (dynamicConfig, account) =>
    account?.liveChatGroupId ?? dynamicConfig?.group_id,
);

export const selectTyping = withChat.selector((state) => state.chat.typing);

export const selectHasUnseenMessages = withChat.selector(
  (state) => state.chat.hasUnseenMessages,
);

export const selectSessionFieldsTS = withChat.selector(
  (state) => state.chat.sessionFieldsTS,
);

export const selectAgent = createSelector(
  [selectChat, withChat.selector((state) => state.chat.agent)],
  findChatAgent,
);

export const selectEvents = withChat.selector((state) => state.chat.events);

export const selectThreadsNextPageId = withChat.selector(
  (state) => state.chat.next_page_id,
);

export const selectCustomer = withChat.selector((state) => state.chat.customer);

export const selectChatVisibility = withChat.selector(
  (state) => state.chat.visibility,
);

declare global {
  interface LazyLoadedSlices {
    chat: ReturnType<typeof persistedReducer>;
  }
}
